import { ExternalLink, Eye, Globe, Hammer, TriangleAlert } from "lucide-react";
import { formatDate, timeAgo } from "@/lib/api";
import { publicUrl } from "./helpers";
import EdgeProbe from "./EdgeProbe";

function Card({ icon: Icon, title, tone, children, testid }) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider ${tone}`}><Icon size={14} />{title}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function PointerBody({ site, d, which, empty, testid }) {
  if (!d) return <p className="text-sm text-slate-500">{empty}</p>;
  const url = site.urls[which];
  const h = site.hosting;
  const host = url && /^https?:/.test(url) ? url.replace(/^https?:\/\//, "").replace(/\/$/, "") : null;
  return (
    <>
      <p className="text-2xl font-bold tabular-nums text-slate-900">n. {d.number}</p>
      <p className="text-xs text-slate-500">compilata {formatDate(d.finished_at, true)}</p>
      {d.target !== h.mode && <p className="mt-2 text-xs text-amber-700" data-testid={`${testid}-other-hosting`}>Compilata per un altro hosting: ricompila per pubblicarla su «{h.label}».</p>}
      {host && <p className="mt-2 truncate font-mono text-xs text-slate-700" title={url} data-testid={`${testid}-host`}>{host}</p>}
      {host && which === "live" && site.domain?.serving && <p className="text-[11px] text-slate-500" data-testid={`${testid}-domain-note`}>Dominio del cliente · {h.live_host} rimanda qui (301)</p>}
      {url && !(host && h.server_only) && <a href={publicUrl(url)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid={testid}>Apri<ExternalLink size={13} /></a>}
      {host && <div className="mt-2"><EdgeProbe key={`${d.id}-${site.suspended}`} siteId={site.id} which={which} /></div>}
    </>
  );
}

export default function SitePointers({ site, current }) {
  const sync = site.hosting?.edge_sync;
  return (
    <div className="space-y-3">
      {sync?.error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="site-edge-sync-error">
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />Ultimo aggiornamento sull'hosting non riuscito ({timeAgo(sync.at)}): {sync.error}. Riprova l'operazione o usa «Sincronizza i siti attivati» in Hosting dei siti.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        <Card icon={Eye} title="Anteprima" tone="text-sky-700" testid="pointer-preview">
          <PointerBody site={site} d={site.preview} which="preview" empty="Nessuna anteprima: compila il sito." testid="open-preview-link" />
        </Card>
        <Card icon={Globe} title={`Online · ${site.hosting?.label || "Studio (interno)"}`} tone="text-emerald-700" testid="pointer-live">
          {site.suspended && <p className="mb-1 text-xs font-medium text-amber-700" data-testid="pointer-live-suspended">{site.active === false ? "Sito archiviato: indirizzi disattivati" : "Sospeso: i visitatori vedono la pagina di manutenzione"}</p>}
          <PointerBody site={site} d={site.live} which="live" empty="Non ancora pubblicato." testid="open-live-link" />
        </Card>
        <Card icon={Hammer} title="Ultima compilazione" tone="text-slate-600" testid="pointer-last">
          {current ? (
            <>
              <p className="text-2xl font-bold tabular-nums text-slate-900">n. {current.number}</p>
              <p className="text-sm text-sky-700" data-testid="pointer-last-phase">{current.status === "queued" ? "In coda…" : `${current.phase_label}…`}</p>
            </>
          ) : site.last_deploy ? (
            <>
              <p className="text-2xl font-bold tabular-nums text-slate-900">n. {site.last_deploy.number}</p>
              <p className={`text-xs ${site.last_deploy.status === "ready" ? "text-slate-500" : "text-red-600"}`}>{site.last_deploy.status === "ready" ? "riuscita" : site.last_deploy.status === "cancelled" ? "annullata" : "non riuscita"} · {timeAgo(site.last_deploy.at)}</p>
            </>
          ) : <p className="text-sm text-slate-500">Mai compilato.</p>}
        </Card>
      </div>
    </div>
  );
}
